import React from "react";
import { motion } from "framer-motion";
import {
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import ApiLoader from "../ui/ApiLoader";

const DashboardComponent = ({ loading, dashboardMetrics }) => {
  return (
    <motion.div
      key="dashboard"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {loading && !dashboardMetrics ? (
        <ApiLoader label="Loading dashboard..." />
      ) : (
        <>
          {/* Dashboard Metrics */}
          {dashboardMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <ClipboardDocumentListIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Incidents</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {dashboardMetrics.metrics?.total_incidents || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                    <ClockIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Incidents</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {dashboardMetrics.metrics?.open_incidents || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-red-100 dark:bg-red-900 rounded-lg">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Critical/High Severity</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {(dashboardMetrics.metrics?.critical_incidents || 0) + 
                       (dashboardMetrics.metrics?.high_incidents || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                    <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">SLA Compliance</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {dashboardMetrics.metrics?.sla_compliance_percentage?.toFixed(1) || 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recent Critical Incidents */}
          {dashboardMetrics?.recent_critical_incidents?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Critical Incidents</h3>
              <div className="space-y-3">
                {dashboardMetrics.recent_critical_incidents.map((incident, index) => (
                  <div key={incident.id || index} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="flex items-center space-x-3">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{incident.incident_no}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{incident.title}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-full text-xs font-medium">
                        {incident.severity?.toUpperCase()}
                      </span>
                      {incident.sla_breached && (
                        <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-full text-xs font-medium">
                          SLA BREACH
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Incidents by Category */}
          {dashboardMetrics?.incidents_by_category && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Incidents by Category</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(dashboardMetrics.incidents_by_category).map(([category, count]) => (
                  <div key={category} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 capitalize">
                      {category.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default DashboardComponent;
